import PropTypes from 'prop-types';
import React from 'react';

import useTimeOfUsable from 'src/util/i18n/timeOfUse/useTimeOfUsable';

/**
 * Renders a time-of-usable entity in a structured list format with price/rate, details, and
 * optional conditions.
 *
 * **Use this component when:** You need to display time-of-use information in a structured,
 * multi-line format within lists or detailed views where each piece of information should be on
 * its own line.
 *
 * **Key differences from DetailedInline:**
 * - Renders content in a structured format with `<li>` elements for description and conditions
 * - Each piece of information (description, conditions) appears on separate lines
 * - Suitable for detailed views, forms, or structured layouts where vertical space is available
 * - Returns a React fragment containing the value followed by list items
 * @param {object} props - Component props
 * @param {object} props.timeOfUsable - The time-of-usable entity containing pricing and time-of-use
 *   configuration.
 * @param {boolean} props.showConditions - Whether to display additional conditions for the clause
 * @param {string} [props.valueField='price'] - The value to be converted and formatted as a
 *   price or rate
 * @returns {React.ReactElement|null} Set of <li> elements containing the formatted
 * clause information with value, time-of-use description, and optional conditions,
 * or null if timeOfUsable is empty
 */
function Detailed({ timeOfUsable, showConditions, valueField = 'price' }) {
  if (!timeOfUsable || Object.keys(timeOfUsable).length === 0) {
    return null;
  }

  const { value, description, conditions } = useTimeOfUsable(timeOfUsable, valueField);

  return (
    <>
      <li style={{ listStyle: 'none' }}>{value}</li>
      <li>
        {description}
      </li>
      {showConditions && (
      <li>
        {conditions}
      </li>
      )}
    </>
  );
}

Detailed.propTypes = {
  timeOfUsable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  showConditions: PropTypes.bool.isRequired,
  valueField: PropTypes.string,
};

export default Detailed;
